import React from 'react';
import Select from 'react-select';
import { trainTypeIcons, categoryLabels } from './trainUtils';

// Same six product categories as the train search, but multi-select here
export const TYPE_FILTERS = [
  { type: 'fern', apiValue: 'longdistance', label: 'Fernverkehr' },
  { type: 'sbahn', apiValue: 'localtrain', label: 'S-Bahn' },
  { type: 'regio', apiValue: 'localtrain', label: 'Nahverkehr' },
  { type: 'ubahn', apiValue: 'metro', label: 'U-Bahn' },
  { type: 'tram', apiValue: 'tram', label: 'Straßenbahn' },
  { type: 'bus', apiValue: 'bus', label: 'Bus' },
];

// Simulated minutes per real second. 2× and 5× fill the gap between following single trains
// at 1× and the sweep at 10×.
export const SPEEDS = [1, 2, 5, 10, 60];

// Shared react-select styling (same tokens as the train search)
export const selectStyles = {
  control: (base) => ({
    ...base,
    background: 'var(--input-bg)',
    borderColor: 'var(--border-color)',
    color: 'var(--text-primary)',
    fontSize: '0.78rem',
    minHeight: '30px',
  }),
  menu: (base) => ({
    ...base,
    background: 'var(--bg-card)',
    border: '1px solid var(--border-color)',
    zIndex: 1200,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 1200 }),
  option: (base, state) => ({
    ...base,
    background: state.isFocused ? 'var(--bg-hover)' : 'transparent',
    color: 'var(--text-primary)',
    fontSize: '0.78rem',
  }),
  singleValue: (base) => ({ ...base, color: 'var(--text-primary)' }),
  input: (base) => ({ ...base, color: 'var(--text-primary)' }),
  placeholder: (base) => ({ ...base, color: 'var(--input-placeholder)', fontSize: '0.78rem' }),
  dropdownIndicator: (base) => ({ ...base, padding: '2px 6px' }),
  clearIndicator: (base) => ({ ...base, padding: '2px 4px' }),
  valueContainer: (base) => ({ ...base, padding: '0 6px' }),
};

/** How many filters narrow the map — shown on the collapsed button and on the phone's FAB */
export const countActiveFilters = (filters) =>
  (filters?.types?.length || 0) +
  (filters?.line ? 1 : 0) + (filters?.serie ? 1 : 0) +
  (filters?.station ? 1 : 0) + (filters?.kbs ? 1 : 0);

const formatClock = (minutes) => {
  const m = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

const MapControls = ({
  collapsed, onToggleCollapsed,
  // Rendered inside the phone's bottom sheet: no own position, no own height cap
  embedded = false,
  mode, onModeChange,
  opDay, onOpDayChange,
  timeMin, onTimeMinChange,
  playing, onPlayingChange,
  speed, onSpeedChange,
  filters, onFiltersChange,
  options,
  stats,
  loading,
}) => {
  const setFilter = (patch) => onFiltersChange({ ...filters, ...patch });

  const toggleType = (type) => {
    const types = filters.types || [];
    setFilter({ types: types.includes(type) ? types.filter(t => t !== type) : [...types, type] });
  };

  const reset = () => onFiltersChange({ types: [], line: null, serie: null, station: null, kbs: null });

  const activeFilterCount = countActiveFilters(filters);

  const findOption = (list, value) => list?.find(o => o.value === value) || null;

  if (collapsed && !embedded) {
    return (
      <div className="map-controls map-controls-collapsed">
        <button className="map-controls-toggle" onClick={onToggleCollapsed} title="Steuerung einblenden">
          <i className="fa fa-sliders"></i>
          {activeFilterCount > 0 && <span className="map-controls-badge">{activeFilterCount}</span>}
        </button>
      </div>
    );
  }

  return (
    <div className={`map-controls${embedded ? ' map-controls-embedded' : ''}`}>
      <div className="map-controls-header">
        <span><i className="fa fa-sliders"></i> Steuerung</span>
        <button
          className="map-controls-collapse"
          onClick={onToggleCollapsed}
          title={embedded ? 'Schließen' : 'Einklappen'}
        >
          <i className={`fa ${embedded ? 'fa-times' : 'fa-chevron-up'}`}></i>
        </button>
      </div>

      {/* Mode */}
      <div className="map-mode-switch">
        <button className={mode === 'live' ? 'active' : ''} onClick={() => onModeChange('live')}>
          <i className="fa fa-circle"></i> Live
        </button>
        <button className={mode === 'point' ? 'active' : ''} onClick={() => onModeChange('point')}>
          <i className="fa fa-clock-o"></i> Zeitpunkt
        </button>
        <button className={mode === 'kbs' ? 'active' : ''} onClick={() => onModeChange('kbs')}>
          <i className="fa fa-signal"></i> KBS-Ampel
        </button>
      </div>

      {/* Operating day + time */}
      {mode !== 'live' && (
        <div className="map-time-block">
          <div className="map-field-row">
            <label className="map-label">Betriebstag</label>
            <input
              type="date"
              className="map-input"
              value={opDay}
              onChange={e => onOpDayChange(e.target.value)}
            />
          </div>

          {mode === 'point' && (
            <>
              <div className="map-field-row">
                <label className="map-label">Uhrzeit</label>
                <input
                  type="time"
                  className="map-input"
                  value={formatClock(timeMin)}
                  onChange={e => {
                    const [h, m] = e.target.value.split(':').map(Number);
                    if (!isNaN(h) && !isNaN(m)) onTimeMinChange(h * 60 + m);
                  }}
                />
              </div>
              <input
                type="range"
                className="map-slider"
                min={0}
                max={1439}
                value={Math.round(timeMin)}
                onChange={e => onTimeMinChange(Number(e.target.value))}
              />
              <div className="map-play-row">
                <button
                  className={`map-play-btn ${playing ? 'active' : ''}`}
                  onClick={() => onPlayingChange(!playing)}
                  title={playing ? 'Pause' : 'Abspielen'}
                >
                  <i className={`fa ${playing ? 'fa-pause' : 'fa-play'}`}></i>
                </button>
                {SPEEDS.map(sp => (
                  <button
                    key={sp}
                    className={`map-speed-btn ${speed === sp ? 'active' : ''}`}
                    onClick={() => onSpeedChange(sp)}
                  >
                    {sp}×
                  </button>
                ))}
                <span className="map-clock">{formatClock(timeMin)}</span>
              </div>
            </>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="map-filter-block">
        <label className="map-label">Produktkategorie</label>
        <div className="map-type-filters">
          {TYPE_FILTERS.map(tf => (
            <button
              key={tf.type}
              className={`map-type-btn ${filters.types?.includes(tf.type) ? 'active' : ''}`}
              onClick={() => toggleType(tf.type)}
              title={tf.label || categoryLabels[tf.apiValue]}
            >
              {trainTypeIcons[tf.type]}
            </button>
          ))}
        </div>

        <div className="map-field-row">
          <label className="map-label">Linie</label>
          <div className="map-select">
            <Select
              options={options.lines}
              value={findOption(options.lines, filters.line)}
              onChange={opt => setFilter({ line: opt?.value || null })}
              isClearable
              placeholder="Alle Linien"
              styles={selectStyles}
              menuPortalTarget={document.body}
            />
          </div>
        </div>

        <div className="map-field-row">
          <label className="map-label">Baureihe</label>
          <div className="map-select">
            <Select
              options={options.series}
              value={findOption(options.series, filters.serie)}
              onChange={opt => setFilter({ serie: opt?.value || null })}
              isClearable
              placeholder="Alle Baureihen"
              styles={selectStyles}
              menuPortalTarget={document.body}
            />
          </div>
        </div>

        <div className="map-field-row">
          <label className="map-label">KBS</label>
          <div className="map-select">
            <Select
              options={options.kbs}
              value={findOption(options.kbs, filters.kbs)}
              onChange={opt => setFilter({ kbs: opt?.value || null })}
              isClearable
              placeholder="Alle Strecken"
              styles={selectStyles}
              menuPortalTarget={document.body}
            />
          </div>
        </div>

        <div className="map-field-row">
          <label className="map-label">Bahnhof</label>
          <div className="map-select">
            <Select
              options={options.stations}
              value={findOption(options.stations, filters.station)}
              onChange={opt => setFilter({ station: opt?.value || null })}
              isClearable
              placeholder="Alle Bahnhöfe"
              styles={selectStyles}
              menuPortalTarget={document.body}
            />
          </div>
        </div>

        {activeFilterCount > 0 && (
          <button className="map-reset-btn" onClick={reset}>
            <i className="fa fa-times"></i> Filter zurücksetzen
          </button>
        )}
      </div>

      <div className="map-controls-footer">
        {loading ? (
          <><i className="fa fa-spinner fa-spin"></i> lädt…</>
        ) : mode === 'kbs' ? (
          <>{stats.kbsCount} Strecken · {stats.kbsTrains} Züge am Betriebstag</>
        ) : (
          <>{stats.visible} Züge sichtbar{stats.cancelled > 0 && ` · ${stats.cancelled} Ausfälle`}</>
        )}
      </div>
    </div>
  );
};

export default MapControls;
